<template>
  <div v-if="disasterNo && isAuth">
    <!-- 共通ヘッダー（タイトル/サブタイトル） -->
    <div class="content-header">
      <h1 class="page-title">
        <i class="fas fa-check-square"></i>
        代替避難所の仮登録
      </h1>
    </div>
    <error-message :error-object="errorObject" />
    <success-message :success-object="successObject" />
    <!-- ステップインジケーター -->
    <div class="step-indicator">
      <div
        class="step"
        :class="{ active: currentStep === 1 }"
        @click="goToStep(1)"
      >
        <div class="step-number">1</div>
        <span>代替の避難先検索</span>
      </div>
      <div class="step-connector"></div>
      <div
        class="step"
        :class="{ active: currentStep === 2 }"
        @click="goToStep(2)"
      >
        <div class="step-number">2</div>
        <span>仮登録</span>
      </div>
    </div>

    <!-- ステップ1: 代替の避難先検索 -->
    <div v-show="currentStep === 1">
      <section>
        <!-- 検索部 -->
        <details
          :open="isOpen"
          v-if="isAuth"
          ref="temporary_registration_search"
        >
          <summary @click.prevent="toggleOpen">検索条件</summary>
          <div class="content-card compact">
            <h2 class="card-title">代替の避難先検索</h2>
            <div
              class="sample-content"
              style="display:flex; gap:8px; align-items:flex-start; flex-wrap:wrap;"
            >
              <div style="flex-shrink:0;">
                <!-- 風向き選択 -->
                <div style="font-weight:500; color:#666; font-size:14px;">
                  ①避難不可の方角を指定してください。<br />（複数可）
                </div>
                <SelectDirection
                  ref="selectDirection"
                  style="margin-left:30px;"
                />
              </div>

              <div
                style="display:flex; gap:16px; flex-wrap:wrap; align-items:flex-start;"
              >
                <!-- モニタリングポスト選択 -->
                <SelectMonitoringPost ref="selectMonitoringPost" />
              </div>

              <div style="flex-shrink:0;">
                <!-- ③代替の避難所の検索 -->
                <div
                  style="display:flex; flex-direction:column; gap:4px; min-width:300px; margin-left:15px;"
                >
                  <div
                    style="margin-bottom:3px; font-weight:500; color:#666; font-size:14px;"
                  >
                    ③代替の避難所を検索できます。
                  </div>

                  <div style="display:flex; flex-direction:column; gap:4px;">
                    <div style="display:flex; align-items:center; gap:8px;">
                      <div class="vtf-field" style="width:120px;">
                        <input
                          v-model="searchFilters.evaMunicipName"
                          type="text"
                          placeholder=" "
                          maxlength="30"
                        />
                        <label>市町名</label>
                      </div>
                      <div class="hint">部分一致</div>
                    </div>
                    <div style="display:flex; align-items:center; gap:8px;">
                      <div class="vtf-field" style="width:120px;">
                        <input
                          v-model="searchFilters.evaFaName"
                          type="text"
                          placeholder=" "
                          maxlength="50"
                        />
                        <label>施設名</label>
                      </div>
                      <div class="hint">部分一致</div>
                    </div>
                    <div style="margin:1px 0;">
                      <div
                        style="margin-bottom:4px; font-weight:500; color:#666; font-size:12px;"
                      >
                        原発からの直線距離
                      </div>
                      <div class="range-field">
                        <input
                          v-model="searchFilters.evaDistanceFrom"
                          type="text"
                          name="evaDistanceFrom"
                          maxlength="6"
                          autocomplete="off"
                          @input="validateRange"
                        />
                        <span>〜</span>
                        <input
                          v-model="searchFilters.evaDistanceTo"
                          type="text"
                          name="evaDistanceTo"
                          maxlength="6"
                          autocomplete="off"
                          @input="validateRange"
                        />
                        <span>km</span>
                      </div>
                    </div>
                  </div>

                  <!-- マッチング条件 -->
                  <div style="border: 2px solid #ccc; padding: 8px;">
                    <h5>★マッチング条件</h5>
                    <div style="display:flex; flex-direction:column;">
                      <label
                        style="display:flex; align-items:center; gap:4px; font-size:14px;"
                      >
                        <input
                          type="radio"
                          name="matchingCondition"
                          value="1"
                          v-model="searchFilters.matchingCondition"
                        />
                        使用困難な避難先から直線距離が近い代替の避難先順
                      </label>
                      <label
                        style="display:flex; align-items:center; gap:4px; font-size:14px;"
                      >
                        <input
                          type="radio"
                          name="matchingCondition"
                          value="2"
                          v-model="searchFilters.matchingCondition"
                        />
                        収容人数の空きが多い代替の避難先順
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div
              style="display:flex; justify-content:flex-end; padding: 0 8px; margin-top:2px;"
            >
                    <v-btn class="btn-primary" @click="clickGetList()">
                      一覧表示
                    </v-btn>
            </div>
          </div>
        </details>

        <!-- 一覧部 -->
        <div class="content-card">
          <div
            style="display:flex; justify-content:space-between; align-items:center; margin-bottom:16px; flex-wrap:wrap;"
          >
            <div
              style="display:flex; align-items:center; gap:16px;flex-wrap:wrap;"
          >
              <h2 class="card-title" style="margin:0;">代替の避難先一覧</h2>
              <select
                v-model="cityFilter"
                style="padding:8px 12px; border:1px solid #c7c7c7; border-radius:4px; font-size:14px; min-width:150px;"
              >
                <option value="">避難先の市町を絞込</option>
                <option
                  v-for="city in uniqueCities"
                  :key="city"
                  :value="city"
                  >{{ city }}</option
                >
              </select>
              <div style="font-size:14px;">
                ※一覧表示した代替の避難先と、調整対象の自治会をマッチングします。
              </div>
            </div>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn class="btn-primary" @click="exportExcel()">
                Excel出力
              </v-btn>
            </v-card-actions>
          </div>
          <div
            class
            :style="{ height: tableHeight() + 'px', overflow: 'auto' }"
          >
            <table class="table adjustment-table tr-table">
              <thead>
                <tr>
                  <th colspan="7">避難先</th>
                  <th colspan="5">避難計画における避難元</th>
                </tr>
                <tr>
                  <th
                    rowspan="2"
                    style="cursor:pointer;"
                    @click="sortBy('eva_municip_name')"
                  >
                    市町名
                    <span v-if="sortKey === 'eva_municip_name'">
                      <i
                        :class="[
                          'fas',
                          sortOrder === 'asc' ? 'fa-sort-up' : 'fa-sort-down'
                        ]"
                      ></i>
                    </span>
                    <span v-else>
                      <i class="fas fa-sort"></i>
                    </span>
                  </th>
                  <th colspan="2">施設名</th>
                  <th
                    rowspan="2"
                    style="cursor:pointer;"
                    @click="sortBy('eva_fa_address')"
                  >
                    住所
                    <span v-if="sortKey === 'eva_fa_address'">
                      <i
                        :class="[
                          'fas',
                          sortOrder === 'asc' ? 'fa-sort-up' : 'fa-sort-down'
                        ]"
                      ></i>
                    </span>
                    <span v-else>
                      <i class="fas fa-sort"></i>
                    </span>
                  </th>
                  <th
                    rowspan="2"
                    style="cursor:pointer;"
                    @click="sortBy('max_capacity')"
                  >
                    収容人数
                    <span v-if="sortKey === 'max_capacity'">
                      <i
                        :class="[
                          'fas',
                          sortOrder === 'asc' ? 'fa-sort-up' : 'fa-sort-down'
                        ]"
                      ></i>
                    </span>
                    <span v-else>
                      <i class="fas fa-sort"></i>
                    </span>
                  </th>
                  <th colspan="2">原発からの</th>
                  <th rowspan="2">自治会名等</th>
                  <th rowspan="2">自治会名等</th>
                  <th rowspan="2">自治会名等</th>
                  <th rowspan="2">自治会名等</th>
                  <th rowspan="2">自治会名等</th>
                </tr>
                <tr>
                  <th style="cursor:pointer;" @click="sortBy('eva_fa_name')">
                    漢字
                    <span v-if="sortKey === 'eva_fa_name'">
                      <i
                        :class="[
                          'fas',
                          sortOrder === 'asc' ? 'fa-sort-up' : 'fa-sort-down'
                        ]"
                      ></i>
                    </span>
                    <span v-else>
                      <i class="fas fa-sort"></i>
                    </span>
                  </th>
                  <th
                    style="cursor:pointer;"
                    @click="sortBy('eva_fa_name_kana')"
                  >
                    フリガナ
                    <span v-if="sortKey === 'eva_fa_name_kana'">
                      <i
                        :class="[
                          'fas',
                          sortOrder === 'asc' ? 'fa-sort-up' : 'fa-sort-down'
                        ]"
                      ></i>
                    </span>
                    <span v-else>
                      <i class="fas fa-sort"></i>
                    </span>
                  </th>
                  <th>方角</th>
                  <th>直線距離</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(data, index) in sortedFacilities"
                  :key="data.key + '_' + index"
                >
                  <td>{{ data.eva_municip_name }}</td>
                  <td>{{ data.eva_fa_name }}</td>
                  <td>{{ data.eva_fa_name_kana }}</td>
                  <td>{{ data.eva_fa_address }}</td>
                  <td>{{ data.max_capacity }}</td>
                  <td>{{ data.direction_from_npp }}</td>
                  <td>{{ data.distance_from_npp }} km</td>
                  <td v-for="i in 5" :key="i">
                    {{
                      data.autonomys && data.autonomys.length > i - 1
                        ? data.autonomys[i - 1].autonomy_name
                        : ""
                    }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>

    <!-- ステップ2: 仮確定 -->
    <div v-show="currentStep === 2">
      <section>
        <!-- 一覧部 -->
        <div class="content-card">
          <div
            style="display:flex; justify-content:space-between; align-items:center; margin-bottom:16px; flex-wrap:wrap;"
          >
            <div
              style="display:flex; align-items:center; gap:16px; flex-wrap:wrap;"
          >
              <h2 class="card-title" style="margin:0;">
                代替避難先の仮登録一覧
              </h2>
              <select
                v-model="tempCityFilter"
                style="padding:8px 12px; border:1px solid #c7c7c7; border-radius:4px; font-size:14px; min-width:150px;"
              >
                <option value="">避難先の市町を絞込</option>
                <option
                  v-for="city in uniqueCitiesTemporary"
                  :key="city"
                  :value="city"
                  >{{ city }}</option
                >
              </select>
              <div style="font-size:14px;" v-if="!showDoneOnly">
                ※調整が必要な自治会の代替避難先として仮登録する場合は、<br />「<label
                  style="color:red;"
                  >候補選択</label
                >」にチェックし「仮登録」を押下してください。
              </div>
              <div style="font-size:14px;" v-if="showDoneOnly">
                ※代替避難先の仮登録を取り消す場合は、<br />「<label
                  style="color:red;"
                  >候補選択</label
                >」のチェックを外し「仮登録取消」を押下してください。
              </div>
            </div>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                v-if="!showDoneOnly"
                class="btn-secondary"
                @click="recalculateCapacity"
              >
                想定収容人数の再集計
              </v-btn>
              <v-btn class="btn-secondary" @click="toggleDoneFilter">
                {{
                  showDoneOnly
                    ? "未仮登録情報のみ表示"
                    : "仮登録済み情報のみ表示"
                }}
              </v-btn>
              <v-btn class="btn-primary" @click="exportExcel()">
                Excel出力
              </v-btn>
              <v-btn class="btn-primary" @click="confirm">
                {{ showDoneOnly ? "仮登録取消" : "仮登録" }}
              </v-btn>
            </v-card-actions>
          </div>
          <div
            class
            :style="{ height: tableHeight() + 'px', overflow: 'auto' }"
          >
            <table class="table adjustment-table tr2-table">
              <thead>
                <tr>
                  <th colspan="4">避難元</th>
                  <th rowspan="3" style="color:red;">候補<br />選択</th>
                  <th colspan="9">避難先</th>
                </tr>
                <tr>
                  <th
                    rowspan="2"
                    style="cursor:pointer;"
                    @click="sortBy('eva_municip_name')"
                  >
                    市町名
                    <span v-if="sortKey === 'eva_municip_name'">
                      <i
                        :class="[
                          'fas',
                          sortOrder === 'asc' ? 'fa-sort-up' : 'fa-sort-down'
                        ]"
                      ></i>
                    </span>
                    <span v-else>
                      <i class="fas fa-sort"></i>
                    </span>
                  </th>
                  <th
                    rowspan="2"
                    style="cursor:pointer;"
                    @click="sortBy('eva_autonomy_name')"
                  >
                    自治会名
                    <span v-if="sortKey === 'eva_autonomy_name'">
                      <i
                        :class="[
                          'fas',
                          sortOrder === 'asc' ? 'fa-sort-up' : 'fa-sort-down'
                        ]"
                      ></i>
                    </span>
                    <span v-else>
                      <i class="fas fa-sort"></i>
                    </span>
                  </th>
                  <th rowspan="2">人口</th>
                  <th
                    rowspan="2"
                    style="cursor:pointer;"
                    @click="sortBy('eva_fa_name')"
                  >
                    使用困難な避難所
                    <span v-if="sortKey === 'eva_fa_name'">
                      <i
                        :class="[
                          'fas',
                          sortOrder === 'asc' ? 'fa-sort-up' : 'fa-sort-down'
                        ]"
                      ></i>
                    </span>
                    <span v-else>
                      <i class="fas fa-sort"></i>
                    </span>
                  </th>
                  <th
                    rowspan="2"
                    style="cursor:pointer;"
                    @click="sortBy('temp_municip_name')"
                  >
                    市町名
                    <span v-if="sortKey === 'temp_municip_name'">
                      <i
                        :class="[
                          'fas',
                          sortOrder === 'asc' ? 'fa-sort-up' : 'fa-sort-down'
                        ]"
                      ></i>
                    </span>
                    <span v-else>
                      <i class="fas fa-sort"></i>
                    </span>
                  </th>
                  <th
                    rowspan="2"
                    style="cursor:pointer;"
                    @click="sortBy('temp_fa_name')"
                  >
                    代替の避難所
                    <span v-if="sortKey === 'temp_fa_name'">
                      <i
                        :class="[
                          'fas',
                          sortOrder === 'asc' ? 'fa-sort-up' : 'fa-sort-down'
                        ]"
                      ></i>
                    </span>
                    <span v-else>
                      <i class="fas fa-sort"></i>
                    </span>
                  </th>
                  <th colspan="2">原発からの</th>
                  <th rowspan="2">収容人数</th>
                  <th colspan="2">避難所間の</th>
                  <th
                    rowspan="2"
                    style="cursor:pointer;"
                    @click="sortBy('plan_capacity')"
                  >
                    避難計画の<br />収容人数
                    <span v-if="sortKey === 'plan_capacity'">
                      <i
                        :class="[
                          'fas',
                          sortOrder === 'asc' ? 'fa-sort-up' : 'fa-sort-down'
                        ]"
                      ></i>
                    </span>
                    <span v-else>
                      <i class="fas fa-sort"></i>
                    </span>
                  </th>
                  <th
                    rowspan="2"
                    style="cursor:pointer;"
                    @click="sortBy('expected_capacity_calc')"
                  >
                    収容人数<br />（想定数）
                    <span v-if="sortKey === 'expected_capacity_calc'">
                      <i
                        :class="[
                          'fas',
                          sortOrder === 'asc' ? 'fa-sort-up' : 'fa-sort-down'
                        ]"
                      ></i>
                    </span>
                    <span v-else>
                      <i class="fas fa-sort"></i>
                    </span>
                  </th>
                </tr>
                <tr>
                  <th>方角</th>
                  <th
                    style="cursor:pointer;"
                    @click="sortBy('temp_distance_from_npp')"
                  >
                    直線距離
                    <span v-if="sortKey === 'temp_distance_from_npp'">
                      <i
                        :class="[
                          'fas',
                          sortOrder === 'asc' ? 'fa-sort-up' : 'fa-sort-down'
                        ]"
                      ></i>
                    </span>
                    <span v-else>
                      <i class="fas fa-sort"></i>
                    </span>
                  </th>
                  <th>方角</th>
                  <th
                    style="cursor:pointer;"
                    @click="sortBy('distance_from_eva')"
                  >
                    直線距離
                    <span v-if="sortKey === 'distance_from_eva'">
                      <i
                        :class="[
                          'fas',
                          sortOrder === 'asc' ? 'fa-sort-up' : 'fa-sort-down'
                        ]"
                      ></i>
                    </span>
                    <span v-else>
                      <i class="fas fa-sort"></i>
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(data, index) in sortedTemporaries"
                  :key="data.key + '_' + index"
                >
                  <td>{{ data.eva_municip_name }}</td>
                  <td>{{ data.eva_autonomy_name }}</td>
                  <td>{{ data.eva_population }}</td>
                  <td>{{ data.eva_fa_name }}</td>
                  <td><input type="checkbox" v-model="data.temp_flg" /></td>
                  <td>{{ data.temp_municip_name }}</td>
                  <td>{{ data.temp_fa_name }}</td>
                  <td>{{ data.temp_direction_from_npp_name }}</td>
                  <td>{{ data.temp_distance_from_npp }} km</td>
                  <td>{{ data.temp_max_capacity }}</td>
                  <td>{{ data.direction_from_eva_name }}</td>
                  <td>{{ data.distance_from_eva }} km</td>
                  <td>{{ data.plan_capacity }}</td>
                  <td>{{ getCalculatedCapacity(data) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
    <ConfirmDialog ref="confirmDialog" />
    <ErrorDialog ref="errorDialog" />
    <v-overlay :value="overlay" opacity="1" color="white">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </div>
  <div v-else>
    <div v-if="disasterNo">
    <error-message :error-object="errorObject" />
  </div>
  </div>
</template>

<script>
import ExcelJS from "exceljs";
import messageUtils from "@/mixins/message-utils";
import { ErrorTodoMessages } from "@/js/constants/error-messages";
import { RepositoryFactory } from "@/js/repository/RepositoryFactory";
import { ScreenId } from "@/js/value-objects/screen-id";

const adjustmentSystemRepository = RepositoryFactory.get("adjustmentSystem");
const radiationMonitoringRepository = RepositoryFactory.get(
  "radiationMonitoring"
);
const temporaryRegistrationRepository = RepositoryFactory.get(
  "temporaryRegistration"
);

const LIST_MAX_HEIGHT = 538;
const LIST_MAX_HEIGHT_2 = 560;
const LIST_MIN_HEIGHT = 104;

const STEP_1 = 1;
const STEP_2 = 2;

export default {
  name: "TemporaryRegistration",
  mixins: [messageUtils],
  components: {
    ErrorMessage: () => import("@/components/ErrorMessage"),
    SuccessMessage: () => import("@/components/SuccessMessage"),
    ErrorDialog: () => import("@/components/adjustment/AdErrorDialog"),
    ConfirmDialog: () => import("@/components/adjustment/AdConfirmDialog"),
    SelectDirection: () => import("@/components/adjustment/SelectDirection"),
    SelectMonitoringPost: () =>
      import("@/components/adjustment/SelectMonitoringPost")
  },
  props: {
    disasterNo: {
      type: String,
      default: ""
    }
  },
  data: () => ({
    currentStep: 1,
    overlay: false,
    isOpen: true,
    isAuth: false,
    isInitError: false,
    cityFilter: "",
    tempCityFilter: "",
    showDoneOnly: false,
    capacityCalculations: {},
    searchFilters: {
      evaMunicipName: "",
      evaFaName: "",
      evaDistanceFrom: null,
      evaDistanceTo: null,
      matchingCondition: "1"
    },
    sortKey: "",
    sortOrder: "asc",
    facilityList: [],
    temporaryList: [],
    directions: [],
    monitoringPosts: [],
    searchPayload: [],
    evaTargetCount: 0,
    maxMatchCount: 0
  }),

  computed: {
    // 一覧の市町名の絞込み
    filteredFacilities() {
      return this.facilityList.filter(facility => {
        if (this.cityFilter && facility.eva_municip_name !== this.cityFilter)
          return false;
        return true;
      });
    },
    // 一覧のソート
    sortedFacilities() {
      const filtered = this.filteredFacilities;
      if (!this.sortKey) return filtered;

      return [...filtered].sort((a, b) => {
        let aVal = a[this.sortKey];
        let bVal = b[this.sortKey];

        // 数値項目の場合は数値に変換
        if (this.sortKey === "max_capacity") {
          aVal = Number(aVal);
          bVal = Number(bVal);
        }

        const modifier = this.sortOrder === "asc" ? 1 : -1;

        if (aVal < bVal) return -1 * modifier;
        if (aVal > bVal) return 1 * modifier;
        return 0;
      });
    },
    // 一覧の市町名の絞込み
    filteredTemporaries() {
      return this.temporaryList.filter(temporary => {
        if (
          this.tempCityFilter &&
          temporary.temp_municip_name !== this.tempCityFilter
        )
          return false;

        // done_flgによるフィルタリング
        if (this.showDoneOnly && !temporary.done_flg) return false;
        if (!this.showDoneOnly && temporary.done_flg) return false;

        return true;
      });
    },
    // 一覧のソート
    sortedTemporaries() {
      const filtered = this.filteredTemporaries;
      if (!this.sortKey) return filtered;

      return [...filtered].sort((a, b) => {
        let aVal = a[this.sortKey];
        let bVal = b[this.sortKey];

        // 数値項目の場合は数値に変換
        if (
          this.sortKey === "temp_distance_from_npp" ||
          this.sortKey === "direction_from_eva_name" ||
          this.sortKey === "plan_capacity" ||
          this.sortKey === "expected_capacity_calc"
        ) {
          aVal = Number(aVal);
          bVal = Number(bVal);
        }

        const modifier = this.sortOrder === "asc" ? 1 : -1;

        if (aVal < bVal) return -1 * modifier;
        if (aVal > bVal) return 1 * modifier;
        return 0;
      });
    },
    // 一覧の市町名絞込みリスト作成
    uniqueCities() {
      const cities = [
        ...new Set(this.facilityList.map(facility => facility.eva_municip_name))
      ];
      return cities.filter(city => city).sort();
    },
    // 一覧の市町名絞込みリスト作成
    uniqueCitiesTemporary() {
      const filteredList = this.temporaryList.filter(temporary => {
        if (this.showDoneOnly && !temporary.done_flg) return false;
        if (!this.showDoneOnly && temporary.done_flg) return false;
        return true;
      });
      const cities = [
        ...new Set(filteredList.map(temporary => temporary.temp_municip_name))
      ];
      return cities.filter(city => city).sort();
    }
  },

  async mounted() {
    await this.initialize();
    if (this.disasterNo === "" || this.isInitError) return;
    await this.fetchMonitoringPosts();
  },

  methods: {
    // 初期処理
    async initialize() {
      this.currentStep = 1;
      this.clearMessage();
      this.overlay = true;
      try {
        this.clearSort();
        /* 権限取得 */
        const payload = {
          disasterNo: this.disasterNo
        };
        await adjustmentSystemRepository
          .list(ScreenId.SF4101, payload)
          .then(response => {
            this.isInitError = false;
            this.isAuth = response.data.isAuth;
          })
          .catch(e => {
            this.isInitError = true;
            this.handleUpdateError(e);
          });
      } catch (error) {
        this.isInitError = true;
        this.handleUpdateError(error);
        console.error("ユーザーの権限取得に失敗しました:", error);
      } finally {
        this.overlay = false;
      }
    },
    // 検索条件表示非表示の時の一覧の高さ調整
    tableHeight() {
      let height = 0;
      if (this.currentStep == STEP_2) {
        height = LIST_MAX_HEIGHT_2;
      } else {
        height = this.isOpen ? LIST_MIN_HEIGHT : LIST_MAX_HEIGHT;
      }
      return height;
    },
    // ページ遷移
    async goToStep(step) {
      if (step == STEP_2) {
        const filtered = this.filteredFacilities;
        if (!filtered.length > 0) {
          await this.$refs.errorDialog.open(ErrorTodoMessages.ERWE48001);
          return;
        }
        const matchCount = filtered.length * this.evaTargetCount;
        if (matchCount > this.maxMatchCount) {
          await this.$refs.errorDialog.open(
            ErrorTodoMessages.ERWE48008(this.maxMatchCount)
          );
          return;
        }
        this.currentStep = step;
        this.clearMessage();
        await this.getList();
        this.showDoneOnly = false;
      } else {
        this.currentStep = step;
        this.isOpen = true;
      }
      this.cityFilter = "";
      this.tempCityFilter = "";
    },
    // 検索条件開閉
    toggleOpen() {
      this.isOpen = !this.isOpen;
    },
    // モニタリングポスト一覧取得
    async fetchMonitoringPosts() {
      this.overlay = true;
      try {
        /* 放射線モニタリング一覧取得 */
        const payload = {};
        await radiationMonitoringRepository
          .list(ScreenId.SF4101, payload)
          .then(response => {
            this.$refs.selectMonitoringPost.setMonitoringPosts(
              response.data.results
            );
          })
          .catch(e => {
            this.handleUpdateError(e);
          });
      } catch (error) {
        this.handleUpdateError(error);
        console.error("モニタリングポスト一覧の取得に失敗しました:", error);
      } finally {
        this.overlay = false;
      }
    },
    validateRange(e) {
      const key = e.target.name;
      let txtValue = e.target.value.replace(/[^0-9.]/g, "");
      let value = parseFloat(txtValue);
      if (isNaN(value)) {
        this.searchFilters[key] = txtValue;
      }
      if (value < 0.0) txtValue = 0.0;
      if (value > 9999.9) txtValue = 9999.9;
      this.searchFilters[key] = txtValue;
    },
    // 一覧のソート処理
    sortBy(key) {
      if (this.sortKey === key) {
        this.sortOrder = this.sortOrder === "asc" ? "desc" : "asc";
      } else {
        this.sortKey = key;
        this.sortOrder = "asc";
      }
    },
    clearSort() {
      this.sortKey = "";
      this.sortOrder = "asc";
    },
    async clickGetList() {
      this.clearMessage();
      await this.getList();
      if (this.currentStep == STEP_2) {
        this.tempCityFilter = "";
      } else {
        this.cityFilter = "";
      }
    },
    // 一覧の取得
    async getList() {
      this.overlay = true;
      try {
        this.clearSort();
        if (this.currentStep == STEP_1) {
          this.directions = await this.$refs.selectDirection.getSelectDirection();
          this.monitoringPosts = await this.$refs.selectMonitoringPost.getMonitoringPosts();

          const payload = {
            disasterNo: this.disasterNo,
            directionList: this.directions,
            stationList: this.monitoringPosts.map(post => post.station_id),
            evaMunicipName: this.searchFilters.evaMunicipName,
            evaFaName: this.searchFilters.evaFaName,
            distanceFrom: this.searchFilters.evaDistanceFrom,
            distanceTo: this.searchFilters.evaDistanceTo
          };
          this.searchPayload = payload;
        } else {
          this.searchPayload.matchingValue = this.searchFilters.matchingCondition;
          if (this.cityFilter !== "")
            this.searchPayload.evaMunicipName = this.cityFilter;
        }

        if (this.currentStep == STEP_2) {
          /* 代替避難先の仮登録一覧取得 */
          await temporaryRegistrationRepository
            .matchingList(ScreenId.SF4101, this.searchPayload)
            .then(response => {
              this.temporaryList = response.data.results;
            })
            .catch(e => {
              this.handleUpdateError(e);
            });
        } else {
          /* 代替の避難先一覧取得 */
          await temporaryRegistrationRepository
            .list(ScreenId.SF4101, this.searchPayload)
            .then(response => {
              this.facilityList = response.data.results;
              this.maxMatchCount = response.data.maxMatchCount;
              this.evaTargetCount = response.data.evaTargetCount;
              this.isOpen = false;
            })
            .catch(e => {
              this.handleUpdateError(e);
            });
        }
      } catch (error) {
        this.handleUpdateError(error);
        if (this.currentStep == STEP_2) {
          console.error("代替避難先の仮登録一覧の取得に失敗しました:", error);
        } else {
          console.error("代替の避難先一覧の取得に失敗しました:", error);
        }
      } finally {
        this.overlay = false;
      }
    },
    // 仮登録データの表示切り替え
    toggleDoneFilter() {
      this.showDoneOnly = !this.showDoneOnly;
    },
    // 計算後の収容人数を取得
    getCalculatedCapacity(data) {
      const baseCapacity = Number(data.expected_capacity) || 0;
      const additionalCapacity =
        this.capacityCalculations[data.temp_fa_id] || 0;
      data.expected_capacity_calc = baseCapacity + additionalCapacity;
      return data.expected_capacity_calc;
    },
    // 想定収容人数の再計算
    async recalculateCapacity() {
      // 計算用項目をクリア
      this.capacityCalculations = {};

      // チェックが付いた行の人口と代替避難所を保持
      const checkedItems = this.temporaryList.filter(item => item.temp_flg);

      // 代替避難所ごとに人口を集計
      const facilityPopulations = {};
      checkedItems.forEach(item => {
        const facilityId = item.temp_fa_id;
        const population = Number(item.eva_population) || 0;

        if (!facilityPopulations[facilityId]) {
          facilityPopulations[facilityId] = 0;
        }
        facilityPopulations[facilityId] += population;
      });

      // 計算用項目に加算値を設定
      this.capacityCalculations = facilityPopulations;
    },
    // EXCEL出力
    async exportExcel() {
      let templateFileName;
      let downloadFileName;
      let dateCell;

      this.overlay = true;
      try {
        if (this.currentStep == STEP_2) {
          if (!this.temporaryList.length > 0) {
            await this.$refs.errorDialog.open(ErrorTodoMessages.ERWE48001);
            return;
          }
          templateFileName = "temporary_registration_list_2.xlsx";
          downloadFileName = "代替避難先の仮登録一覧";
          dateCell = "O2";
        } else {
          if (!this.facilityList.length > 0) {
            await this.$refs.errorDialog.open(ErrorTodoMessages.ERWE48001);
            return;
          }
          templateFileName = "temporary_registration_list_1.xlsx";
          downloadFileName = "代替の避難先一覧";
          dateCell = "M2";
        }

        /* タイムスタンプ算出 */
        let now = new Date();
        let year = now.getFullYear();
        let month = ("00" + (now.getMonth() + 1)).slice(-2);
        let day = ("00" + now.getDate()).slice(-2);
        let hour = ("00" + now.getHours()).slice(-2);
        let minute = ("00" + now.getMinutes()).slice(-2);
        let timeStamp = year + month + day + hour + minute;
        let timeStamp2 =
          year + "/" + month + "/" + day + " " + hour + ":" + minute;

        // 固定のExcelファイルを取得（publicフォルダに配置されていると仮定）
        const response = await fetch("/template/" + templateFileName);
        const arrayBuffer = await response.arrayBuffer();

        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(arrayBuffer);
        const worksheet = workbook.getWorksheet(1); // 最初のシートを取得

        // 罫線スタイルを定義
        const borderStyle = {
          style: "thin",
          color: { argb: "FF000000" }
        };

        //5行目まではヘッダー
        let rowNo = 5;
        if (this.currentStep == STEP_2) {
          this.temporaryList.forEach(data => {
            if (
              this.tempCityFilter &&
              data.temp_municip_name !== this.tempCityFilter
            )
              return;

            if (this.showDoneOnly && !data.done_flg) return;
            if (!this.showDoneOnly && data.done_flg) return;

            rowNo++;
            const row = worksheet.insertRow(rowNo, [
              rowNo - 5,
              data.eva_municip_name,
              data.eva_autonomy_name,
              Number(data.eva_population),
              data.eva_fa_name,
              data.temp_flg_db ? "○" : "",
              data.temp_municip_name,
              data.temp_fa_name,
              data.temp_direction_from_npp_name,
              data.temp_distance_from_npp + " km",
              Number(data.temp_max_capacity),
              data.direction_from_eva_name,
              data.distance_from_eva + " km",
              Number(data.plan_capacity),
              Number(data.expected_capacity)
            ]);

            // 各セルに罫線を設定
            row.eachCell(cell => {
              cell.border = {
                top: borderStyle,
                left: borderStyle,
                bottom: borderStyle,
                right: borderStyle
              };
            });
          });
        } else {
          this.facilityList.forEach(data => {
            if (this.cityFilter && data.eva_municip_name !== this.cityFilter)
              return;

            rowNo++;
            const autonomyNames = [];
            for (let i = 0; i < 5; i++) {
              autonomyNames.push(
                data.autonomys && data.autonomys[i]
                  ? data.autonomys[i].autonomy_name
                  : ""
              );
            }

            const row = worksheet.insertRow(rowNo, [
              rowNo - 5,
              data.eva_municip_name,
              data.eva_fa_name,
              data.eva_fa_name_kana,
              data.eva_fa_address,
              Number(data.max_capacity),
              data.direction_from_npp,
              data.distance_from_npp + " km",
              ...autonomyNames
            ]);

            // 各セルに罫線を設定
            row.eachCell(cell => {
              cell.border = {
                top: borderStyle,
                left: borderStyle,
                bottom: borderStyle,
                right: borderStyle
              };
            });
          });
        }

        const cell = worksheet.getCell(dateCell);
        cell.value = "出力日時：" + timeStamp2;

        // 編集済みファイルをダウンロード
        const editedBuffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([editedBuffer], {
          type:
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        });

        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = downloadFileName + "_" + timeStamp + ".xlsx";
        link.click();
      } catch (error) {
        console.error("EXCEL出力に失敗しました:", error);
      } finally {
        this.overlay = false;
      }
    },
    // 仮登録処理
    async confirm() {
      try {
        if (!this.temporaryList.length > 0) {
          await this.$refs.errorDialog.open(ErrorTodoMessages.ERWE48001);
          return;
        }

        // 更新確認ダイアログを表示
        let message = this.showDoneOnly
          ? "仮登録した避難先を取り消しします。\nよろしいですか？"
          : "避難先の候補として仮登録します。\nよろしいですか？";
        let btnText = this.showDoneOnly ? "仮登録取消" : "仮登録";
        const result = await this.$refs.confirmDialog.open(message, btnText);
        if (!result) return;

        this.clearMessage();
        this.overlay = true;

        const paramList = [];
        this.temporaryList.forEach(data => {
          if (
            this.tempCityFilter &&
            data.temp_municip_name !== this.tempCityFilter
          )
            return;

          if (this.showDoneOnly && !data.done_flg) return;
          if (!this.showDoneOnly && data.done_flg) return;

          if (this.showDoneOnly && data.temp_flg) return;
          if (!this.showDoneOnly && !data.temp_flg) return;

          paramList.push({
            tempFlg: data.temp_flg || false,
            autonomyCode: data.eva_autonomy_code,
            evaFaId: data.eva_fa_id,
            tempFaId: data.temp_fa_id,
            directionFromFa: data.direction_from_eva,
            distanceFromFa: data.distance_from_eva
          });
        });

        if (!paramList.length > 0) {
          let msg = this.showDoneOnly
            ? ErrorTodoMessages.ERWE48003
            : ErrorTodoMessages.ERWE48002;
          await this.$refs.errorDialog.open(msg);
          return;
        }

        const payload = {
          disasterNo: this.disasterNo,
          list: paramList
        };

        let isSuccess = false;
        await temporaryRegistrationRepository
          .update(ScreenId.SF4101, payload)
          .then(response => {
            isSuccess = true;
            this.handleSuccess(response);
            if (!this.showDoneOnly) {
              this.$emit("update-status", {
                menu: "temporary_registration",
                completed: true
              });
            }
          })
          .catch(error => {
            this.handleUpdateError(error);
          });

        if (isSuccess) {
          // 登録後の最新データを取得する
          await this.getList();
        }
      } catch (error) {
        this.handleUpdateError(error);
        console.error("避難先の候補の仮登録に失敗しました:", error);
      } finally {
        this.overlay = false;
      }
    }
  }
};
</script>
